import type { ReactNode } from "react";
import styles from "./Text.module.css";

type TextSize = "xs" | "sm" | "base" | "lg";
type TextColor = "primary" | "secondary" | "muted" | "inherit" | "on-dark" | "on-dark-muted" | "emerald" | "success" | "warning" | "danger";
type TextWeight = "normal" | "medium" | "semibold" | "bold";
type TextElement = "p" | "span" | "div";

interface TextProps {
  readonly as?: TextElement;
  readonly size?: TextSize;
  readonly color?: TextColor;
  readonly weight?: TextWeight;
  readonly children: ReactNode;
  readonly className?: string;
}

const SIZE_MAP = {
  xs: "var(--type-xs-size)",
  sm: "var(--type-small-size)",
  base: "var(--type-body-size)",
  lg: "var(--type-body-lg-size)",
} as const;

const LINE_HEIGHT_MAP = {
  xs: "var(--type-xs-line-height)",
  sm: "var(--type-small-line-height)",
  base: "var(--type-body-line-height)",
  lg: "var(--type-body-lg-line-height)",
} as const;

const COLOR_MAP = {
  primary: "var(--color-text-primary)",
  secondary: "var(--color-text-secondary)",
  muted: "var(--color-text-muted)",
  inherit: "inherit",
  "on-dark": "var(--color-text-on-dark)",
  "on-dark-muted": "var(--color-text-on-dark-muted)",
  emerald: "var(--color-accent-solid)",
  success: "var(--color-accent-solid)",
  warning: "var(--color-warning-solid)",
  danger: "var(--color-danger-solid)",
} as const;

const WEIGHT_MAP = {
  normal: "var(--weight-normal)",
  medium: "var(--weight-medium)",
  semibold: "var(--weight-semibold)",
  bold: "var(--weight-bold)",
} as const;

export function Text({
  as: Element = "p",
  size = "base",
  color = "primary",
  weight = "normal",
  children,
  className,
}: TextProps) {
  return (
    <Element
      className={`${styles.text} ${className ?? ""}`}
      style={{
        fontSize: SIZE_MAP[size],
        lineHeight: LINE_HEIGHT_MAP[size],
        color: COLOR_MAP[color],
        fontWeight: WEIGHT_MAP[weight],
      }}
    >
      {children}
    </Element>
  );
}
